/**
 * How the grid reads a value: as text for the search box, as something to
 * compare for sorting, and as a yes-or-no for each kind of column filter.
 *
 * A column says what it holds with `meta.type`, one of four:
 *
 *   text    matched by "contains", ignoring case
 *   enum    a short list of values, picked from with checkboxes
 *   number  a min and a max
 *   date    a from and a to, both days inclusive
 *
 * The accessor hands back the raw value (a string, a number, a Date) and
 * `undefined` where there is nothing to say. Blank is its own answer rather
 * than an empty string or a zero, because the two are different statements -
 * "worth nothing" and "nobody has priced it" read the same as 0 and must not
 * sort together.
 *
 * Pure: no React, no DOM, so the rules can be run in Node.
 */
import { memo, getMemoOptions } from '@tanstack/react-table';
import { parseDate, formatDate, startOfDay, pad2 } from '../../lib/dates.js';

/** Nothing there: undefined, null, an empty string, NaN or an invalid date. */
export const isBlank = v => v === undefined || v === null || v === ''
  || (typeof v === 'number' && Number.isNaN(v))
  || (v instanceof Date && Number.isNaN(v.getTime()));

/** A Date for a date column's value, whichever shape the page handed over. */
export const asDate = v => {
  if (isBlank(v)) return null;
  if (v instanceof Date) return v;
  return parseDate(v);
};

/** A number for a number column's value, or null. */
export const asNumber = v => {
  if (isBlank(v)) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/** yyyy-mm-dd in local time: what a date input speaks and what a file sorts by. */
export const isoDay = d => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** The key an enum value is filtered by. Blank has a key of its own, ''. */
export const enumKey = v => (isBlank(v) ? '' : String(v));

/* --------------------------------------------------------------- dates */

/**
 * The windows people actually ask for, resolved against the clock when they
 * are picked rather than stored as a mode, so a tab left open overnight
 * cannot go on calling yesterday "today". The same three the CRM filter bar
 * offers, worked out the same way.
 */
export const DATE_PRESETS = [
  ['today', 'Today', () => { const n = new Date(); return [n, n]; }],
  ['7d', 'Last 7 days', () => {
    const n = new Date(); const a = new Date(); a.setDate(a.getDate() - 6); return [a, n];
  }],
  ['month', 'This month', () => {
    const n = new Date(); return [new Date(n.getFullYear(), n.getMonth(), 1), n];
  }]
];

/** A preset as the { from, to } a date filter holds. */
export const presetRange = key => {
  const hit = DATE_PRESETS.find(p => p[0] === key);
  if (!hit) return undefined;
  const [a, b] = hit[2]();
  return { from: isoDay(a), to: isoDay(b) };
};

/** Which preset a range is, today, if any - so the chip can say so. */
export const presetOf = range => {
  if (!range) return null;
  const hit = DATE_PRESETS.find(([key]) => {
    const r = presetRange(key);
    return r.from === range.from && r.to === range.to;
  });
  return hit ? hit[0] : null;
};

/* ------------------------------------------------------------- filters */

/** Is the number inside { min, max }? Either end may be missing. */
export function inNumberRange(value, range) {
  const lo = asNumber(range && range.min);
  const hi = asNumber(range && range.max);
  if (lo === null && hi === null) return true;
  /* Blank cannot be "at least 100" - it drops out as soon as a bound is set,
     the rule the CRM date window already follows. */
  const n = asNumber(value);
  if (n === null) return false;
  return (lo === null || n >= lo) && (hi === null || n <= hi);
}

/** Is the day inside { from, to }? Both ends inclusive, either may be missing. */
export function inDateRange(value, range) {
  const from = range && range.from ? parseDate(range.from) : null;
  const to = range && range.to ? parseDate(range.to) : null;
  if (!from && !to) return true;
  const d = asDate(value);
  if (!d) return false;
  const day = startOfDay(d);
  return (!from || day >= startOfDay(from)) && (!to || day <= startOfDay(to));
}

/** Nothing set in a filter value, so the filter should not exist at all. */
export function isEmptyFilter(type, value) {
  if (value === undefined || value === null) return true;
  if (type === 'enum') return !Array.isArray(value);
  if (type === 'number') return asNumber(value.min) === null && asNumber(value.max) === null;
  if (type === 'date') return !value.from && !value.to;
  return !String(value).trim();
}

/**
 * The TanStack filter function for a column of this type.
 *
 * `textOf` reads a row the way the column reads as text, which is not
 * always its sort value: a contact sorts by address but is found by its
 * phone number too.
 */
export function filterFor(type, textOf) {
  let fn;
  if (type === 'enum') {
    /* An array of the keys still ticked. An empty array is a real answer -
       everything unticked - and shows nothing, which is what was asked. */
    fn = (row, id, picked) => !Array.isArray(picked) || picked.includes(enumKey(row.getValue(id)));
  } else if (type === 'number') {
    fn = (row, id, range) => inNumberRange(row.getValue(id), range);
  } else if (type === 'date') {
    fn = (row, id, range) => inDateRange(row.getValue(id), range);
  } else {
    fn = (row, id, q) => {
      const want = String(q || '').trim().toLowerCase();
      return !want || textOf(row.original).toLowerCase().includes(want);
    };
  }
  fn.autoRemove = value => isEmptyFilter(type, value);
  return fn;
}

/* ------------------------------------------------------------- sorting */

/* Numeric-aware and case-blind, so "Item 9" comes before "Item 10" and
   "acme" sits beside "Acme". */
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/** Ascending comparison of two present values of this type. */
export function compareFor(type) {
  if (type === 'number') return (a, b) => asNumber(a) - asNumber(b);
  if (type === 'date') return (a, b) => asDate(a) - asDate(b);
  return (a, b) => collator.compare(String(a), String(b));
}

/**
 * The sorted row model, with blanks last whichever way a column runs.
 *
 * TanStack's own puts undefined last only by returning early, before any
 * later sort gets a say, so every unowned lead came out in no particular
 * order under "owner, then requested" - and the unowned are half the
 * board. Here two blanks tie and the next sort decides, a blank always
 * follows a value, and whatever still ties keeps the order the page handed
 * over (newest first, on both boards).
 */
export function sortedRowModel() {
  return table => memo(
    () => [table.getState().sorting, table.getPreSortedRowModel()],
    (sorting, model) => {
      if (!model.rows.length || !sorting || !sorting.length) return model;
      const specs = sorting
        .map(s => ({ ...s, column: table.getColumn(s.id) }))
        .filter(s => s.column && s.column.getCanSort())
        .map(s => ({ ...s, fn: s.column.getSortingFn() }));
      if (!specs.length) return model;

      const rows = [...model.rows].sort((a, b) => {
        for (const s of specs) {
          const blankA = isBlank(a.getValue(s.id));
          const blankB = isBlank(b.getValue(s.id));
          if (blankA || blankB) {
            if (blankA && blankB) continue;
            return blankA ? 1 : -1;
          }
          const c = s.fn(a, b, s.id);
          if (c) return s.desc ? -c : c;
        }
        return a.index - b.index;
      });
      return { rows, flatRows: rows, rowsById: model.rowsById };
    },
    getMemoOptions(table.options, 'debugTable', 'getSortedRowModel')
  );
}

/* ---------------------------------------------------------------- text */

/**
 * A value as the words somebody would search for.
 *
 * A date is both ways it is written - "19 Sep 2026" as the page shows it and
 * 2026-09-19 as a file does - so either finds it; a blank answers to the
 * column's own name for nothing ("Unowned"), if it has one.
 */
export function textOfValue(type, value, { dateFmt = 'long', blank = '' } = {}) {
  if (isBlank(value)) return blank;
  if (type === 'date') {
    const d = asDate(value);
    return d ? `${formatDate(d, false, dateFmt)} ${isoDay(d)}` : '';
  }
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

/**
 * A value as it goes into a file: a date as yyyy-mm-dd, a list joined, a
 * flag in words, and nothing as nothing. Numbers stay numbers, so a
 * spreadsheet can add them up.
 */
export function exportCell(type, value) {
  if (isBlank(value)) return '';
  if (type === 'date' || value instanceof Date) {
    const d = asDate(value);
    return d ? isoDay(d) : '';
  }
  if (Array.isArray(value)) return value.filter(x => !isBlank(x)).join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value;
  return String(value);
}
